/**
 * 管理者ルート定義（AdminLayout）
 *
 * @file admin-routes.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * 管理者向けページのルーティング設定。
 * AdminLayoutを静的importし、52ルートを管理。
 */
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@shared/components/ProtectedRoute';
import { UserRole } from '@shared/types';
import { lazyWithI18n } from '@shared/i18n';
import { I18nLoadingScreen } from '@shared/contexts/LanguageContext';
import AdminLayout from '@/layouts/AdminLayout';

const lazyAdminPage = (
  importFn: () => Promise<{ default: React.ComponentType }>,
  pageNamespaces: string[]
) => {
  return lazyWithI18n(importFn, [
    ...pageNamespaces,
    'layout/AdminLayout',
    'common',
    'errors'
  ]);
};

// ========================================
// 販売者ページ（認証不要）
// ========================================
const AdminLoginPage = lazyAdminPage(
  () => import('../pages/AdminLoginPage'),
  ['admin/AdminLoginPage']
);  // A-001: 管理者ログイン

const AdminForcePasswordChangePage = lazyAdminPage(
  () => import('../pages/AdminForcePasswordChangePage'),
  ['admin/AdminForcePasswordChangePage']
);  // A-014A: 強制パスワード変更

// ========================================
// 販売者ページ（認証必須）
// ========================================
const AdminDashboardPage = lazyAdminPage(
  () => import('../pages/AdminDashboardPage'),
  ['admin/AdminDashboardPage']
);  // A-002: 管理者ダッシュボード

const AdminBuyerPage = lazyAdminPage(
  () => import('../pages/AdminBuyerPage'),
  ['admin/AdminBuyerPage']
);  // A-003: 顧客管理

const AdminProductListPage = lazyAdminPage(
  () => import('../pages/AdminProductListPage'),
  ['admin/AdminProductListPage']
);  // A-004A: 商品管理

const AdminProductCategoryPage = lazyAdminPage(
  () => import('../pages/AdminProductCategoryPage'),
  ['admin/AdminProductCategoryPage']
);  // A-004B: カテゴリー管理

const AdminOrderPage = lazyAdminPage(
  () => import('../pages/AdminOrderPage'),
  ['admin/AdminOrderPage']
);  // A-005: 注文管理

const AdminInventoryPage = lazyAdminPage(
  () => import('../pages/AdminInventoryPage'),
  ['admin/AdminInventoryPage']
);  // A-006: 倉庫管理

const AdminLogisticsIncomingPage = lazyAdminPage(
  () => import('../pages/AdminLogisticsIncomingPage'),
  ['admin/AdminLogisticsIncomingPage']
);  // A-007A: 入荷管理

const AdminLogisticsOutgoingPage = lazyAdminPage(
  () => import('../pages/AdminLogisticsOutgoingPage'),
  ['admin/AdminLogisticsOutgoingPage']
);  // A-007B: 出荷管理

const AdminLogisticsRepackagingPage = lazyAdminPage(
  () => import('../pages/AdminLogisticsRepackagingPage'),
  ['admin/AdminLogisticsRepackagingPage']
);  // A-007C: 再梱包管理

const AdminLogisticsTrackingPage = lazyAdminPage(
  () => import('../pages/AdminLogisticsTrackingPage'),
  ['admin/AdminLogisticsTrackingPage']
);  // A-007D: 追跡情報管理

const AdminAnalyticsSalesPage = lazyAdminPage(
  () => import('../pages/AdminAnalyticsSalesPage'),
  ['admin/AdminAnalyticsSalesPage']
);  // A-008: 売上分析

const AdminMarketingCampaignPage = lazyAdminPage(
  () => import('../pages/AdminMarketingCampaignPage'),
  ['admin/AdminMarketingCampaignPage']
);  // A-009A: キャンペーン管理

const AdminMarketingCouponPage = lazyAdminPage(
  () => import('../pages/AdminMarketingCouponPage'),
  ['admin/AdminMarketingCouponPage']
);  // A-009B: クーポン管理

const AdminMarketingBannerPage = lazyAdminPage(
  () => import('../pages/AdminMarketingBannerPage'),
  ['admin/AdminMarketingBannerPage']
);  // A-009C: バナー管理

const AdminMarketingSeoPage = lazyAdminPage(
  () => import('../pages/AdminMarketingSeoPage'),
  ['admin/AdminMarketingSeoPage']
);  // A-009D: SEO設定

const AdminChatSupportPage = lazyAdminPage(
  () => import('../pages/AdminChatSupportPage'),
  ['admin/AdminChatSupportPage']
);  // A-010: チャットサポート管理

const AdminSellerListPage = lazyAdminPage(
  () => import('../pages/AdminSellerListPage'),
  ['admin/AdminSellerListPage']
);  // A-011A: 販売者一覧

const AdminSellerApprovePage = lazyAdminPage(
  () => import('../pages/AdminSellerApprovePage'),
  ['admin/AdminSellerApprovePage']
);  // A-011B: 販売者申請承認

const AdminSellerContractPage = lazyAdminPage(
  () => import('../pages/AdminSellerContractPage'),
  ['admin/AdminSellerContractPage']
);  // A-011C: 販売者契約管理

const AdminSellerAnalyticsPage = lazyAdminPage(
  () => import('../pages/AdminSellerAnalyticsPage'),
  ['admin/AdminSellerAnalyticsPage']
);  // A-011D: 販売者売上分析

const AdminPaymentListPage = lazyAdminPage(
  () => import('../pages/AdminPaymentListPage'),
  ['admin/AdminPaymentListPage']
);  // A-012A: 決済一覧

const AdminPaymentSettlementPage = lazyAdminPage(
  () => import('../pages/AdminPaymentSettlementPage'),
  ['admin/AdminPaymentSettlementPage']
);  // A-012B: 精算管理

const AdminPaymentAnomalyPage = lazyAdminPage(
  () => import('../pages/AdminPaymentAnomalyPage'),
  ['admin/AdminPaymentAnomalyPage']
);  // A-012C: 異常検知

const AdminPaymentSettingPage = lazyAdminPage(
  () => import('../pages/AdminPaymentSettingPage'),
  ['admin/AdminPaymentSettingPage']
);  // A-012D: 決済設定

const AdminSettingLanguagePage = lazyAdminPage(
  () => import('../pages/AdminSettingLanguagePage'),
  ['admin/AdminSettingLanguagePage']
);  // A-013A: 言語設定

const AdminSettingCurrencyPage = lazyAdminPage(
  () => import('../pages/AdminSettingCurrencyPage'),
  ['admin/AdminSettingCurrencyPage']
);  // A-013B: 通貨設定

const AdminSettingPermissionPage = lazyAdminPage(
  () => import('../pages/AdminSettingPermissionPage'),
  ['admin/AdminSettingPermissionPage']
);  // A-013C: 権限設定

const AdminSettingApiPage = lazyAdminPage(
  () => import('../pages/AdminSettingApiPage'),
  ['admin/AdminSettingApiPage']
);  // A-013D: API連携設定

const AdminSettingBackupPage = lazyAdminPage(
  () => import('../pages/AdminSettingBackupPage'),
  ['admin/AdminSettingBackupPage']
);  // A-013E: バックアップ設定

const AdminSettingDisplayPage = lazyAdminPage(
  () => import('../pages/AdminSettingDisplayPage'),
  ['admin/AdminSettingDisplayPage']
);  // A-013F: 表示設定

const AdminAdministratorListPage = lazyAdminPage(
  () => import('../pages/AdminAdministratorListPage'),
  ['admin/AdminAdministratorListPage']
);  // A-014A: 管理者アカウント一覧

const AdminAdministratorSecurityPage = lazyAdminPage(
  () => import('../pages/AdminAdministratorSecurityPage'),
  ['admin/AdminAdministratorSecurityPage']
);  // A-014B: 管理者セキュリティ設定

const AdminAdministratorSessionPage = lazyAdminPage(
  () => import('../pages/AdminAdministratorSessionPage'),
  ['admin/AdminAdministratorSessionPage']
);  // A-014C: 管理者ログインセッション

const AdminAdministratorLogPage = lazyAdminPage(
  () => import('../pages/AdminAdministratorLogPage'),
  ['admin/AdminAdministratorLogPage']
);  // A-014D: 管理者活動ログ

const AdminSystemInfoPage = lazyAdminPage(
  () => import('../pages/AdminSystemInfoPage'),
  ['admin/AdminSystemInfoPage']
);  // A-021: システム情報

const AdminExternalSitePage = lazyAdminPage(
  () => import('../pages/AdminExternalSitePage'),
  ['admin/AdminExternalSitePage']
);  // A-015: 外部サイト管理

const AdminProxyPurchasePage = lazyAdminPage(
  () => import('../pages/AdminProxyPurchasePage'),
  ['admin/AdminProxyPurchasePage']
);  // A-016: 代理購入管理

const AdminProxyProductRegisterPage = lazyAdminPage(
  () => import('../pages/AdminProxyProductRegisterPage'),
  ['admin/AdminProxyProductRegisterPage']
);  // A-017: 仕入商品登録

const AdminHelpPage = lazyAdminPage(
  () => import('../pages/AdminHelpPage'),
  ['admin/AdminHelpPage']
);  // A-018: FAQ・ヘルプページ（管理者向け）

const AdminNotificationPage = lazyAdminPage(
  () => import('../pages/AdminNotificationPage'),
  ['admin/AdminNotificationPage']
);  // A-019: 通知設定

const AdminAccountSettingsPage = lazyAdminPage(
  () => import('../pages/AdminAccountSettingsPage'),
  ['admin/AdminAccountSettingsPage']
);  // A-020: アカウント設定

const AdminStaticContentPage = lazyAdminPage(
  () => import('../pages/AdminStaticContentPage'),
  ['admin/AdminStaticContentPage']
);  // A-022: 静的コンテンツ管理

const AdminHelpFaqPage = lazyAdminPage(
  () => import('../pages/AdminHelpFaqPage'),
  ['admin/AdminHelpFaqPage']
);  // A-023A: FAQ設定

const AdminHelpCategoryPage = lazyAdminPage(
  () => import('../pages/AdminHelpCategoryPage'),
  ['admin/AdminHelpCategoryPage']
);  // A-023B: FAQカテゴリー設定

const AdminHelpArticlePage = lazyAdminPage(
  () => import('../pages/AdminHelpArticlePage'),
  ['admin/AdminHelpArticlePage']
);  // A-023C: FAQヘルプ記事設定

const AdminShippingMethodPage = lazyAdminPage(
  () => import('../pages/AdminShippingMethodPage'),
  ['admin/AdminShippingMethodPage']
);  // A-024A: 配送設定/配送方法タブ

const AdminShippingCarrierPage = lazyAdminPage(
  () => import('../pages/AdminShippingCarrierPage'),
  ['admin/AdminShippingCarrierPage']
);  // A-024B: 配送設定/配送業者タブ

const AdminNotFoundPage = lazyAdminPage(
  () => import('../pages/AdminNotFoundPage'),
  []
);  // 404: 未定義URL

const AdminRoutes: React.FC = () => {
  return (
    <Routes>
      {/* A-001: 管理者ログイン */}
      <Route path="login" element={
        <Suspense fallback={<I18nLoadingScreen />}>
          <AdminLoginPage />
        </Suspense>
      } />

      {/* A-014A: 強制パスワード変更（AdminLayout 外・認証必須） */}
      <Route path="/admin/force-password-change" element={
        <ProtectedRoute adminOnly>
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminForcePasswordChangePage />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminLayout />
        </ProtectedRoute>
      }>
        {/* A-002: 管理者ダッシュボード */}
        <Route index element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminDashboardPage />
          </Suspense>
        } />

        {/* A-003: 顧客管理 */}
        <Route path="buyers" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminBuyerPage />
          </Suspense>
        } />

        {/* A-004A: 商品管理 */}
        <Route path="products/list" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminProductListPage />
          </Suspense>
        } />

        {/* A-004B: カテゴリー管理 */}
        <Route path="products/categories" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminProductCategoryPage />
          </Suspense>
        } />

        {/* A-005: 注文管理 */}
        <Route path="orders" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminOrderPage />
          </Suspense>
        } />

        {/* A-006: 倉庫管理 */}
        <Route path="inventory" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminInventoryPage />
          </Suspense>
        } />

        {/* A-007A: 入荷管理 */}
        <Route path="logistics/incoming" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminLogisticsIncomingPage />
          </Suspense>
        } />

        {/* A-007B: 出荷管理 */}
        <Route path="logistics/outgoing" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminLogisticsOutgoingPage />
          </Suspense>
        } />

        {/* A-007C: 再梱包管理 */}
        <Route path="logistics/repackaging" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminLogisticsRepackagingPage />
          </Suspense>
        } />

        {/* A-007D: 追跡情報管理 */}
        <Route path="logistics/tracking" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminLogisticsTrackingPage />
          </Suspense>
        } />

        {/* A-024A/B: 配送設定（/admin/shipping は配送業者タブへリダイレクト） */}
        <Route path="shipping" element={<Navigate to="/admin/shipping/carriers" replace />} />
        <Route path="shipping/carriers" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminShippingCarrierPage />
          </Suspense>
        } />
        <Route path="shipping/methods" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminShippingMethodPage />
          </Suspense>
        } />

        {/* A-008: 売上分析 */}
        <Route path="analytics" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminAnalyticsSalesPage />
          </Suspense>
        } />

        {/* A-009A: キャンペーン管理 */}
        <Route path="marketing/campaigns" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminMarketingCampaignPage />
          </Suspense>
        } />

        {/* A-009B: クーポン管理 */}
        <Route path="marketing/coupons" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminMarketingCouponPage />
          </Suspense>
        } />

        {/* A-009C: バナー管理 */}
        <Route path="marketing/banners" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminMarketingBannerPage />
          </Suspense>
        } />

        {/* A-009D: SEO設定 */}
        <Route path="marketing/seo" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminMarketingSeoPage />
          </Suspense>
        } />

        {/* A-010: チャットサポート管理 */}
        <Route path="chat-support" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminChatSupportPage />
          </Suspense>
        } />

        {/* A-011A: 販売者一覧 */}
        <Route path="sellers/list" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminSellerListPage />
          </Suspense>
        } />

        {/* A-011B: 申請承認 */}
        <Route path="sellers/approve" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminSellerApprovePage />
          </Suspense>
        } />

        {/* A-011C: 契約管理 */}
        <Route path="sellers/contracts" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminSellerContractPage />
          </Suspense>
        } />

        {/* A-011D: 売上分析 */}
        <Route path="sellers/analytics" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminSellerAnalyticsPage />
          </Suspense>
        } />

        {/* A-012A: 決済一覧 */}
        <Route path="payments/list" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminPaymentListPage />
          </Suspense>
        } />

        {/* A-012B: 精算管理 */}
        <Route path="payments/settlements" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminPaymentSettlementPage />
          </Suspense>
        } />

        {/* A-012C: 異常検知 */}
        <Route path="payments/anomaly" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminPaymentAnomalyPage />
          </Suspense>
        } />

        {/* A-012D: 決済設定 */}
        <Route path="payments/settings" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminPaymentSettingPage />
          </Suspense>
        } />

        {/* A-013A: 言語設定 */}
        <Route path="settings/languages" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminSettingLanguagePage />
          </Suspense>
        } />

        {/* A-013B: 通貨設定 */}
        <Route path="settings/currencies" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminSettingCurrencyPage />
          </Suspense>
        } />

        {/* A-013C: 権限設定（super_admin 専用） */}
        <Route path="settings/permissions" element={
          <ProtectedRoute requiredRole={[UserRole.SUPER_ADMIN]}>
            <Suspense fallback={<I18nLoadingScreen />}>
              <AdminSettingPermissionPage />
            </Suspense>
          </ProtectedRoute>
        } />

        {/* A-013D: API連携設定（super_admin 専用） */}
        <Route path="settings/api" element={
          <ProtectedRoute requiredRole={[UserRole.SUPER_ADMIN]}>
            <Suspense fallback={<I18nLoadingScreen />}>
              <AdminSettingApiPage />
            </Suspense>
          </ProtectedRoute>
        } />

        {/* A-013E: バックアップ設定（super_admin 専用） */}
        <Route path="settings/backup" element={
          <ProtectedRoute requiredRole={[UserRole.SUPER_ADMIN]}>
            <Suspense fallback={<I18nLoadingScreen />}>
              <AdminSettingBackupPage />
            </Suspense>
          </ProtectedRoute>
        } />

        {/* A-013F: 表示設定 */}
        <Route path="settings/display" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminSettingDisplayPage />
          </Suspense>
        } />

        {/* A-014A: 管理者アカウント（super_admin / admin 専用） */}
        <Route path="administrator/list" element={
          <ProtectedRoute requiredRole={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
            <Suspense fallback={<I18nLoadingScreen />}>
              <AdminAdministratorListPage />
            </Suspense>
          </ProtectedRoute>
        } />

        {/* A-014B: セキュリティ設定 */}
        <Route path="administrator/security" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminAdministratorSecurityPage />
          </Suspense>
        } />

        {/* A-014C: ログインセッション */}
        <Route path="administrator/sessions" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminAdministratorSessionPage />
          </Suspense>
        } />

        {/* A-014D: 活動ログ */}
        <Route path="administrator/logs" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminAdministratorLogPage />
          </Suspense>
        } />

        {/* A-021: システム情報 */}
        <Route path="system-info" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminSystemInfoPage />
          </Suspense>
        } />

        {/* A-015: 外部サイト管理 */}
        <Route path="external-sites" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminExternalSitePage />
          </Suspense>
        } />

        {/* A-016: 代理購入管理 */}
        <Route path="proxy-purchases" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminProxyPurchasePage />
          </Suspense>
        } />

        {/* A-017: 仕入商品登録 */}
        <Route path="proxy-products/new" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminProxyProductRegisterPage />
          </Suspense>
        } />

        {/* A-018: FAQ・ヘルプページ（管理者向け） */}
        <Route path="help" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminHelpPage />
          </Suspense>
        } />

        {/* A-019: 通知設定 */}
        <Route path="notifications" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminNotificationPage />
          </Suspense>
        } />

        {/* A-020: アカウント設定 */}
        <Route path="account" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminAccountSettingsPage />
          </Suspense>
        } />

        {/* A-022: 静的コンテンツ管理 */}
        <Route path="static-contents" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminStaticContentPage />
          </Suspense>
        } />

        {/* A-023A: FAQ設定 */}
        <Route path="faq-settings" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminHelpFaqPage />
          </Suspense>
        } />
        <Route path="faq-settings/categories" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminHelpCategoryPage />
          </Suspense>
        } />
        <Route path="faq-settings/articles" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <AdminHelpArticlePage />
          </Suspense>
        } />
      </Route>

      {/* 404 Not Found - 全ての未定義ルートをキャッチ */}
      <Route path="*" element={
        <Suspense fallback={<I18nLoadingScreen />}>
          <AdminNotFoundPage />
        </Suspense>
      } />
    </Routes>
  );
};

export default AdminRoutes;
